import type { LetterPickerErrorData } from '../components/learning/LetterPicker';
import { SIGHT_WORDS_SET } from '../constants/sightWords';

interface WordSpan {
    word: string;      // original token
    clean: string;     // lowercase, punctuation stripped
    start: number;     // char position in expectedText (inclusive)
    end: number;       // char position (exclusive)
}

/** Tokenize text into word spans with character offsets */
function tokenize(text: string): WordSpan[] {
    const spans: WordSpan[] = [];
    const regex = /\S+/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        const word = match[0];
        const clean = word.toLowerCase().replace(/[^a-z']/g, '').replace(/^'+|'+$/g, '');
        if (clean) {
            spans.push({
                word,
                clean,
                start: match.index,
                end: match.index + word.length,
            });
        }
    }

    return spans;
}

/**
 * Detect which sight words in a sentence were spelled correctly vs. incorrectly.
 * Uses LetterPicker error data to determine per-word accuracy.
 */
/**
 * Detect sight word results from typing-mode word results.
 * Each entry has a word and whether the user typed it correctly.
 */
export function detectSightWordResultsFromWords(
    wordResults: Array<{ word: string; isCorrect: boolean }>,
): Array<{ word: string; correct: boolean }> {
    const results: Array<{ word: string; correct: boolean }> = [];
    for (const wr of wordResults) {
        const clean = wr.word.toLowerCase().replace(/[^a-z']/g, '').replace(/^'+|'+$/g, '');
        if (clean && SIGHT_WORDS_SET.has(clean)) {
            results.push({ word: clean, correct: wr.isCorrect });
        }
    }
    return results;
}

/**
 * Detect which sight words in a sentence were spelled correctly vs. incorrectly.
 * Uses LetterPicker error data to determine per-word accuracy.
 */
export function detectSightWordResults(
    data: LetterPickerErrorData,
): Array<{ word: string; correct: boolean }> {
    if (!data.expectedText) return [];

    const spans = tokenize(data.expectedText);
    const errorPositions = new Set(data.errors.map(e => e.position));
    const revealedPositions = new Set(data.revealedPositions);
    const results: Array<{ word: string; correct: boolean }> = [];

    for (const span of spans) {
        if (!SIGHT_WORDS_SET.has(span.clean)) continue;

        // Check if any error or hint-reveal falls within this word's range
        let hasError = false;
        for (let pos = span.start; pos < span.end; pos++) {
            if (errorPositions.has(pos) || revealedPositions.has(pos)) {
                hasError = true;
                break;
            }
        }

        results.push({ word: span.clean, correct: !hasError });
    }

    return results;
}
